// Client for the lab telemetry ingest service (BeaconApps/telemetry-ingest).
//
// The service is separate from the DTX backend, so it is called directly
// rather than through the app's GraphQL/REST wrappers. It is optional: with no
// VUE_APP_LAB_TELEMETRY_ENDPOINT configured, `isEnabled()` is false and the
// live-test features stay hidden instead of failing on every poll.
//
// Every request carries the user's DTX factory token. From it the service
// decides which tests this person may see: the tests on their factory's
// machines, in any factory role.

import Config from '@/config'
import { getTokenStorage } from '@/api/session'

const endpoint = () => (process.env.VUE_APP_LAB_TELEMETRY_ENDPOINT || '').replace(/\/+$/, '')

export const isEnabled = () => endpoint() !== ''

const authHeaders = () => {
  const token = getTokenStorage().getItem(Config.localstorageKeys.FACTORY)
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// Errors carry the HTTP status and the service's code (e.g. not_shared,
// session_expired) so the dashboard can say what happened.
const request = async (path, { method = 'GET', body } = {}) => {
  const response = await fetch(endpoint() + path, {
    method,
    cache: 'no-store',
    headers: { ...authHeaders(), ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined
  })
  if (!response.ok) {
    let message = `Lab telemetry service returned ${response.status}`
    let code = ''
    try {
      const payload = await response.json()
      if (payload?.error) message = payload.error
      code = payload?.code || ''
    } catch (err) {
      // Not JSON; keep the status message.
    }
    const error = new Error(message)
    error.status = response.status
    error.code = code
    throw error
  }
  return response.json()
}

const getJSON = (path) => request(path)

// Runs for the given machines (DTX factory resource IDs), newest first. The
// lab agent on each rig is configured with its machine's resource ID, which is
// what ties a streaming test to a card on the Machines & Tools page.
export const listRunsForMachines = async (resourceIds, { status } = {}) => {
  if (!resourceIds?.length) return []
  const params = new URLSearchParams()
  resourceIds.forEach(id => params.append('equipment_id', id))
  if (status) params.set('status', status)
  const body = await getJSON(`/v1/test-runs?${params}`)
  return body.test_runs || []
}

export const getRun = (runId) => getJSON(`/v1/test-runs/${encodeURIComponent(runId)}`)

// Rows strictly after `since` (the timestamp of the last row already held),
// or the whole run when `since` is omitted.
export const getRunData = (runId, { since } = {}) => {
  const params = new URLSearchParams()
  if (since) params.set('since', since)
  const qs = params.toString()
  return getJSON(`/v1/test-runs/${encodeURIComponent(runId)}/data${qs ? `?${qs}` : ''}`)
}
